import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  CalendarDaysIcon,
  PlayIcon,
  StopIcon,
  ClockIcon,
  TruckIcon,
  UsersIcon,
  MapPinIcon,
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  DocumentTextIcon,
  ArrowPathIcon
} from '@heroicons/react/24/outline'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import {
  getTripSessions,
  startTripSession,
  endTripSession,
  getTodaysTripSessions,
  getActiveTripSessions,
  type TripSession,
  type GetTripSessionsParams
} from '@/api/tripsApi'
import { useAuth } from '@/hooks/useAuth'
import toast from 'react-hot-toast'
import { format, parseISO, differenceInMinutes } from 'date-fns'

interface TripSessionsManagerProps {
  institutionId?: string
  driverId?: string // If provided, show only this driver's sessions
  viewMode?: 'all' | 'today' | 'active' | 'driver'
}

const TripSessionsManager: React.FC<TripSessionsManagerProps> = ({
  institutionId,
  driverId,
  viewMode = 'today'
}) => {
  const { user } = useAuth()
  const [sessions, setSessions] = useState<TripSession[]>([])
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [actioningSessionId, setActioningSessionId] = useState<string | null>(null)
  const [selectedSession, setSelectedSession] = useState<TripSession | null>(null)
  const [showStartModal, setShowStartModal] = useState(false)
  const [showEndModal, setShowEndModal] = useState(false)
  const [endNotes, setEndNotes] = useState('')

  // Load sessions on mount and when parameters change
  useEffect(() => {
    loadSessions()
  }, [institutionId, driverId, viewMode])

  // Auto-refresh active sessions every 30 seconds
  useEffect(() => {
    if (viewMode === 'active' || viewMode === 'today') {
      const interval = setInterval(() => {
        refreshSessions()
      }, 30000)
      
      return () => clearInterval(interval)
    }
  }, [viewMode])

  const loadSessions = async () => {
    try {
      setLoading(true)
      
      let response
      
      switch (viewMode) {
        case 'today':
          response = await getTodaysTripSessions(institutionId)
          break
        case 'active':
          response = await getActiveTripSessions(institutionId)
          break
        case 'driver':
          if (driverId) {
            const params: GetTripSessionsParams = {
              driverId,
              institutionId,
              limit: 20
            }
            response = await getTripSessions(params)
          } else {
            response = { data: { sessions: [] } }
          }
          break
        default:
          response = await getTripSessions({ 
            institutionId,
            limit: 50,
            sortBy: 'startTime',
            sortOrder: 'desc'
          })
      }
      
      setSessions(response.data.sessions || [])
      
    } catch (error) {
      console.error('Failed to load trip sessions:', error)
      toast.error('Failed to load trip sessions')
    } finally {
      setLoading(false)
    }
  }

  const refreshSessions = async () => {
    try {
      setRefreshing(true)
      await loadSessions()
    } catch (error) {
      console.error('Failed to refresh sessions:', error)
    } finally {
      setRefreshing(false)
    }
  }

  const openStartModal = (session: TripSession) => {
    setSelectedSession(session)
    setShowStartModal(true)
  }

  const handleStartSession = async (session: TripSession) => {
    // Get session ID - handle both 'id' and '_id' from backend
    const sessionId = session.id || (session as any)._id
    if (!sessionId) {
      toast.error('Session ID is missing')
      return
    }

    try {
      setActioningSessionId(sessionId)
      const response = await startTripSession(sessionId)

      // Update local state - use 'active' status (shows as "In Progress" in UI)
      setSessions(prev => prev.map(s => {
        const sId = s.id || (s as any)._id
        return sId === sessionId
          ? { ...s, status: 'active', actualStartTime: new Date().toISOString() }
          : s
      }))

      toast.success(`Trip "${session.sessionName}" is now in progress!`)
      setShowStartModal(false)
      setSelectedSession(null)

    } catch (error: any) {
      console.error('Failed to start session:', error)
      toast.error(error.response?.data?.message || 'Failed to start session')
    } finally {
      setActioningSessionId(null)
    }
  }

  const handleEndSession = async (session: TripSession, notes?: string) => {
    // Get session ID - handle both 'id' and '_id' from backend
    const sessionId = session.id || (session as any)._id
    if (!sessionId) {
      toast.error('Session ID is missing')
      return
    }

    try {
      setActioningSessionId(sessionId)
      const response = await endTripSession(sessionId, notes)

      // Update local state
      setSessions(prev => prev.map(s => {
        const sId = s.id || (s as any)._id
        return sId === sessionId
          ? { ...s, status: 'completed', actualEndTime: new Date().toISOString(), driverNotes: notes }
          : s
      }))

      toast.success(`Trip session "${session.sessionName}" completed successfully!`)
      setShowEndModal(false)
      setSelectedSession(null)
      setEndNotes('')
      
    } catch (error: any) {
      console.error('Failed to end session:', error)
      toast.error(error.response?.data?.message || 'Failed to end session')
    } finally {
      setActioningSessionId(null)
    }
  }

  const openEndModal = (session: TripSession) => {
    setSelectedSession(session)
    setShowEndModal(true)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'bg-gray-100 text-gray-800'
      case 'active': return 'bg-green-100 text-green-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      case 'cancelled': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getStatusLabel = (status: string) => {
    switch (status) {
      case 'scheduled': return 'Scheduled'
      case 'active': return 'In Progress'
      case 'completed': return 'Completed'
      case 'cancelled': return 'Cancelled'
      default: return status
    }
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'scheduled': return <ClockIcon className="w-4 h-4" />
      case 'active': return <PlayIcon className="w-4 h-4" />
      case 'completed': return <CheckCircleIcon className="w-4 h-4" />
      case 'cancelled': return <XCircleIcon className="w-4 h-4" />
      default: return <ClockIcon className="w-4 h-4" />
    }
  }

  const canStartSession = (session: TripSession) => {
    if (session.status !== 'scheduled') return false
    if (driverId && session.trip?.primaryDriverId !== driverId) return false
    return true
  }

  const canEndSession = (session: TripSession) => {
    if (session.status !== 'active') return false
    if (driverId && session.trip?.primaryDriverId !== driverId) return false
    return true
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <TruckIcon className="w-8 h-8 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Trip Sessions</h2>
            <p className="text-sm text-gray-600">
              {viewMode === 'today' && 'Today\'s Sessions'}
              {viewMode === 'active' && 'Active Sessions'}
              {viewMode === 'driver' && 'My Sessions'}
              {viewMode === 'all' && 'All Sessions'}
            </p>
          </div>
        </div>

        <Button
          variant="secondary"
          onClick={refreshSessions}
          disabled={refreshing}
          className="flex items-center space-x-2"
        >
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </Button>
      </div>

      {/* Sessions List */}
      <div className="space-y-4">
        {sessions.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <TruckIcon className="mx-auto w-12 h-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Trip Sessions</h3>
            <p className="text-gray-600">
              {viewMode === 'today' && 'No sessions scheduled for today'}
              {viewMode === 'active' && 'No active sessions at the moment'}
              {viewMode === 'driver' && 'No sessions assigned to you'}
              {viewMode === 'all' && 'No trip sessions found'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {sessions.map((session) => (
              <motion.div
                key={session.id || (session as any)._id}
                layout
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className={`
                  border rounded-lg p-4 hover:shadow-md transition-shadow
                  ${session.status === 'active' ? 'border-green-300 bg-green-50' : 'border-gray-200'}
                `}
              >
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">
                      {session.sessionName}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <span className={`inline-flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(session.status)}`}>
                        {getStatusIcon(session.status)}
                        <span>{getStatusLabel(session.status)}</span>
                      </span>
                      <span className="text-xs text-gray-500">
                        {session.trip?.tripType}
                      </span>
                    </div>
                  </div>

                  <div className="flex space-x-2">
                    {canStartSession(session) && (
                      <Button
                        size="sm"
                        onClick={() => openStartModal(session)}
                        disabled={actioningSessionId === (session.id || (session as any)._id)}
                        className="flex items-center space-x-1 bg-green-600 hover:bg-green-700"
                      >
                        <PlayIcon className="w-4 h-4" />
                        <span>Start</span>
                      </Button>
                    )}

                    {canEndSession(session) && (
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => openEndModal(session)}
                        disabled={actioningSessionId === (session.id || (session as any)._id)}
                        className="flex items-center space-x-1 bg-red-600 hover:bg-red-700 text-white"
                      >
                        <StopIcon className="w-4 h-4" />
                        <span>End Trip</span>
                      </Button>
                    )}
                  </div>
                </div>

                {/* Session Details */}
                <div className="space-y-2 text-sm">
                  <div className="flex items-center space-x-2 text-gray-600">
                    <ClockIcon className="w-4 h-4" />
                    <span>
                      {format(parseISO(session.startTime), 'HH:mm')}
                      {session.endTime && ` - ${format(parseISO(session.endTime), 'HH:mm')}`}
                    </span>
                  </div>

                  <div className="flex items-center space-x-2 text-gray-600">
                    <UsersIcon className="w-4 h-4" />
                    <span>{session.studentAttendance?.length || 0} students enrolled</span>
                  </div>

                  {session.actualStartTime && (
                    <div className="flex items-center space-x-2 text-gray-600">
                      <CheckCircleIcon className="w-4 h-4" />
                      <span>
                        Started at {format(parseISO(session.actualStartTime), 'HH:mm')}
                        {session.actualEndTime && 
                          ` • Duration: ${differenceInMinutes(parseISO(session.actualEndTime), parseISO(session.actualStartTime))} min`
                        }
                      </span>
                    </div>
                  )}

                  {session.driverNotes && (
                    <div className="flex items-start space-x-2 text-gray-600">
                      <DocumentTextIcon className="w-4 h-4 mt-0.5" />
                      <span className="text-xs">{session.driverNotes}</span>
                    </div>
                  )}

                  {session.incidents && session.incidents.length > 0 && (
                    <div className="flex items-center space-x-2 text-orange-600">
                      <ExclamationTriangleIcon className="w-4 h-4" />
                      <span>{session.incidents.length} incident{session.incidents.length !== 1 ? 's' : ''}</span>
                    </div>
                  )}
                </div>

                {/* Student Attendance Summary */}
                {session.studentAttendance && session.studentAttendance.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-gray-500">Attendance:</span>
                      <div className="flex space-x-3">
                        <span className="text-green-600">
                          {session.studentAttendance.filter(s => s.status === 'boarded' || s.status === 'picked_up').length} boarded
                        </span>
                        <span className="text-yellow-600">
                          {session.studentAttendance.filter(s => s.status === 'waiting').length} waiting
                        </span>
                        <span className="text-red-600">
                          {session.studentAttendance.filter(s => s.status === 'missed').length} missed
                        </span>
                      </div>
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>

      {/* Start Trip Modal */}
      <AnimatePresence>
        {showStartModal && selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center space-x-3 mb-4">
                <PlayIcon className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-semibold text-gray-900">Start Trip</h3>
              </div>

              <div className="mb-6">
                <p className="text-gray-600 mb-4">
                  You are about to start the following trip:
                </p>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <TruckIcon className="w-5 h-5 text-green-600" />
                    <span className="font-semibold text-gray-900">{selectedSession.sessionName}</span>
                  </div>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center space-x-2 text-gray-600">
                      <ClockIcon className="w-4 h-4" />
                      <span>
                        {format(parseISO(selectedSession.startTime), 'HH:mm')}
                        {selectedSession.endTime && ` - ${format(parseISO(selectedSession.endTime), 'HH:mm')}`}
                      </span>
                    </div>

                    <div className="flex items-center space-x-2 text-gray-600">
                      <UsersIcon className="w-4 h-4" />
                      <span>{selectedSession.studentAttendance?.length || 0} students</span>
                    </div>
                  </div>

                  {selectedSession.trip?.tripType && (
                    <div className="text-xs text-gray-500">
                      Trip Type: <span className="capitalize">{selectedSession.trip.tripType.replace('_', ' ')}</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowStartModal(false)
                    setSelectedSession(null)
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleStartSession(selectedSession)}
                  disabled={actioningSessionId === (selectedSession.id || (selectedSession as any)._id)}
                  className="flex-1 flex items-center justify-center space-x-2 bg-green-600 hover:bg-green-700"
                >
                  {actioningSessionId === (selectedSession.id || (selectedSession as any)._id) ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <PlayIcon className="w-4 h-4" />
                  )}
                  <span>Start Trip</span>
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* End Session Modal */}
      <AnimatePresence>
        {showEndModal && selectedSession && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-xl p-6 max-w-md w-full mx-4"
            >
              <div className="flex items-center space-x-3 mb-4">
                <StopIcon className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">End Trip Session</h3>
              </div>

              <div className="mb-4">
                <p className="text-gray-600 mb-2">
                  Are you sure you want to end "{selectedSession.sessionName}"?
                </p>
                
                <div className="bg-gray-50 rounded-lg p-3 text-sm">
                  <div className="flex justify-between">
                    <span>Started:</span>
                    <span>{selectedSession.actualStartTime ? format(parseISO(selectedSession.actualStartTime), 'HH:mm') : 'Not started'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Students:</span>
                    <span>{selectedSession.studentAttendance?.length || 0}</span>
                  </div>
                </div>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Driver Notes (Optional)
                </label>
                <textarea
                  value={endNotes}
                  onChange={(e) => setEndNotes(e.target.value)}
                  rows={3}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  placeholder="Add any notes about the trip..."
                />
              </div>

              <div className="flex space-x-3">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setShowEndModal(false)
                    setSelectedSession(null)
                    setEndNotes('')
                  }}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={() => handleEndSession(selectedSession, endNotes || undefined)}
                  disabled={actioningSessionId === selectedSession.id}
                  className="flex-1 flex items-center justify-center space-x-2"
                >
                  {actioningSessionId === selectedSession.id ? (
                    <LoadingSpinner size="sm" />
                  ) : (
                    <StopIcon className="w-4 h-4" />
                  )}
                  <span>End Session</span>
                </Button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

export default TripSessionsManager